import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { Upload } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { useToast } from '@/lib/hooks/use-toast'
import { useUpdatePDFAgreement } from '../services/useUpdatePDFAgreement'

/**
 * Botão para anexar/upar um ou mais arquivos de contrato para o cliente atual.
 * - Envia via multipart/form-data.
 * - Aceita PDF.
 * - Invalida caches de listas de agreements após upload.
 * - Mostra toasts de sucesso/erro.
 */
export function UploadContractButton({ idAgreement }: { idAgreement?: string }) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { mutateAsync: updatePDFAgreement } = useUpdatePDFAgreement()

  const openPicker = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    inputRef.current?.click()
  }

  const handleFiles = async (files: FileList | null, e?: React.SyntheticEvent) => {
    e?.stopPropagation()
    if (!files || files.length === 0) return
    if (!idAgreement) {
      toast({ title: 'Erro no upload', description: 'ID do contrato não informado.', variant: 'destructive' })
      return
    }

    setUploading(true)
    try {
      const file = files[0]
      await updatePDFAgreement({ id: idAgreement, minuta: file })

      toast({ title: 'Contrato anexado', description: 'PDF enviado com sucesso.' })
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['agreements'] }),
        queryClient.invalidateQueries({ queryKey: ['agreements', idAgreement] })
      ])
    } catch (err: any) {
      const message = err?.response?.data?.message ?? err?.message ?? 'Falha ao anexar contrato.'
      toast({ title: 'Erro no upload', description: message, variant: 'destructive' })
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept=".pdf"
        onChange={e => handleFiles(e.currentTarget.files)}
      />
      <Button
        size="sm"
        onClick={openPicker}
        disabled={uploading}
        className="bg-primary text-white hover:-primary-hover"
      >
        <Upload className="h-4 w-4 mr-2" />
        {uploading ? 'Enviando...' : 'Anexar contrato'}
      </Button>
    </>
  )
}
